import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Flask, UploadSimple, Trash, CaretDown, CaretUp, WarningCircle, FilePdf, ChartLineUp, CalendarPlus } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { ProtocolCard } from "@/components/ProtocolCard";

const FLAG = { low: ["ниже нормы", "#4dabf5"], high: ["выше нормы", "#ff3b30"], normal: ["норма", "#00e676"] };
const fmtD = (ds) => new Date(ds + "T12:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });

const ReportCard = ({ r, onDelete, onDyn, onRetest }) => {
  const [open, setOpen] = useState(false);
  const [onlyAbn, setOnlyAbn] = useState(false);
  const markers = onlyAbn ? r.markers.filter((m) => m.flag !== "normal") : r.markers;
  return (
    <div className="glass-card p-4" data-testid={`lab-report-${r.id}`}>
      <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`lab-toggle-${r.id}`}>
        <FilePdf size={20} className="text-[#e879f9] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate">{r.title}</div>
          <div className="text-[10px] text-muted-foreground num">{fmtD(r.date)} · {r.markers.length} показателей</div>
        </div>
        {r.abnormal > 0 ? (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-[#ff9100]" data-testid={`lab-abnormal-${r.id}`}>
            <WarningCircle size={13} weight="fill" /> {r.abnormal} откл.
          </span>
        ) : (
          <span className="text-[10px] font-semibold text-[#00e676]">всё в норме</span>
        )}
        <button onClick={(e) => { e.stopPropagation(); onRetest(r.id); }} data-testid={`lab-retest-${r.id}`}
          title="Запланировать пересдачу" disabled={r.abnormal === 0}
          className="text-muted-foreground hover:text-[#4dabf5] disabled:opacity-30"><CalendarPlus size={16} /></button>
        <button onClick={(e) => { e.stopPropagation(); onDelete(r.id); }} data-testid={`lab-delete-${r.id}`}
          className="text-muted-foreground hover:text-red-400"><Trash size={15} /></button>
        {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
      </div>
      {open && (
        <div className="mt-3">
          {r.summary && <p className="text-xs text-muted-foreground italic mb-3">{r.summary}</p>}
          <label className="flex items-center gap-2 text-[11px] text-muted-foreground mb-2 cursor-pointer w-fit">
            <input type="checkbox" checked={onlyAbn} onChange={(e) => setOnlyAbn(e.target.checked)} data-testid={`lab-filter-${r.id}`} />
            только отклонения
          </label>
          <div className="space-y-1" data-testid={`lab-markers-${r.id}`}>
            {markers.map((m, i) => (
              <button key={i} onClick={() => onDyn(m.name)} data-testid={`lab-marker-${r.id}-${i}`}
                className="w-full flex items-center gap-2 rounded-md bg-white/[0.03] border border-white/[0.06] px-2.5 py-1.5 text-left hover:border-[#e879f9]/40 transition-colors">
                <span className="text-xs flex-1 min-w-0 truncate">{m.name}</span>
                <b className="num text-xs">{m.value} <span className="text-muted-foreground font-normal">{m.unit}</span></b>
                {m.ref_low != null && m.ref_high != null && (
                  <span className="text-[9px] text-muted-foreground num hidden sm:inline">({m.ref_low}–{m.ref_high})</span>
                )}
                <span className="text-[10px] font-semibold shrink-0" style={{ color: FLAG[m.flag][1] }}>{FLAG[m.flag][0]}</span>
              </button>
            ))}
            {markers.length === 0 && <p className="text-xs text-muted-foreground">Отклонений нет 🎉</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default function LabsPage() {
  const [reports, setReports] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dyn, setDyn] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/labs").then((r) => setReports(r.data.reports)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    try {
      setBusy(true);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/labs/upload", fd, { timeout: 180000 });
      if (!data.report) toast.error(data.summary || "Не удалось распознать");
      else { toast.success(`Распознано: ${data.report.markers.length} показателей`); load(); }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const openDyn = async (name) => {
    const { data } = await api.get(`/labs/dynamics?name=${encodeURIComponent(name)}`).catch(() => ({ data: null }));
    if (data) setDyn(data);
  };

  const remove = async (id) => {
    await api.delete(`/labs/${id}`).catch(() => {});
    load();
  };

  const retest = async (id) => {
    try {
      const { data } = await api.post(`/labs/${id}/schedule-retest`);
      const d = fmtD(data.date);
      toast.success(data.already ? `Пересдача уже в календаре: ${d}` : `Добавлено в календарь: ${d}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="fade-in max-w-3xl" data-testid="labs-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5">
          <Flask size={14} weight="fill" className="text-[#e879f9]" /> Лабораторные показатели
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Анализы</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Загрузите PDF из лаборатории или фото бланка — AI извлечёт показатели, подсветит отклонения и учтёт их в советах AI-коуча.
        </p>
      </div>

      <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="labs-upload-btn"
        className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#e879f9] text-black text-sm font-bold disabled:opacity-50 hover:brightness-110 transition-all mb-5">
        <UploadSimple size={18} weight="bold" /> {busy ? "AI разбирает документ..." : "Загрузить PDF или фото"}
      </button>
      <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" data-testid="labs-file-input"
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />

      <ProtocolCard />

      <div className="space-y-3" data-testid="labs-list">
        {reports?.map((r) => <ReportCard key={r.id} r={r} onDelete={remove} onDyn={openDyn} onRetest={retest} />)}
        {reports && reports.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8" data-testid="labs-empty">
            Пока нет отчётов — загрузите первый PDF с результатами
          </p>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground mt-6">
        ⚕️ Интерпретация AI не является медицинским диагнозом или назначением. Обсуждайте результаты с врачом.
      </p>

      <Dialog open={!!dyn} onOpenChange={(v) => !v && setDyn(null)}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ChartLineUp size={18} className="text-[#e879f9]" />{dyn?.name}</DialogTitle></DialogHeader>
          {dyn && (dyn.points.length >= 2 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dyn.points} margin={{ top: 10, right: 10, bottom: 0, left: -15 }}>
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10, fill: "#8b93a7" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#8b93a7" }} tickLine={false} axisLine={false} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "rgba(15,18,28,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => [`${v} ${dyn.points[0]?.unit || ""}`]} labelFormatter={fmtD} />
                {dyn.points[0]?.ref_low != null && <ReferenceLine y={dyn.points[0].ref_low} stroke="#4dabf5" strokeDasharray="4 4" />}
                {dyn.points[0]?.ref_high != null && <ReferenceLine y={dyn.points[0].ref_high} stroke="#ff3b30" strokeDasharray="4 4" />}
                <Line dataKey="value" stroke="#e879f9" strokeWidth={2.5} dot={{ r: 4 }} type="monotone" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="dyn-single">
              {dyn.points.length === 1
                ? `Пока одна точка: ${dyn.points[0].value} ${dyn.points[0].unit} (${fmtD(dyn.points[0].date)}). График появится после следующей сдачи.`
                : "Числовых значений этого показателя не найдено."}
            </p>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
